import { ApiError } from '@/api/ApiError'
import { AddMembersResults } from '@/features/OrgMembers/components/AddMembersResults'
import { MembersEditor } from '@/features/OrgMembers/components/MembersEditor'
import { useMemberRows } from '@/features/OrgMembers/hooks/useMemberRows'
import { invalidateOrgUsers } from '@/features/OrgMembers/lib/invalidateOrgUsers'
import {
  type MemberOutcome,
  isEveryMemberAdded,
  pairResultsWithRows,
  shouldRefreshMembers,
} from '@/features/OrgMembers/lib/memberOutcomes'
import {
  type MemberRow,
  buildAddMembersRequest,
  canSubmitRows,
} from '@/features/OrgMembers/lib/memberRows'
import { useAddOrganizationMembers } from '@/generated/http-clients/v3ApiComponents'
import {
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import {
  Alert,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const KEY_PREFIX = 'orgDetails.members.addMembers'
const SUCCESS_TOAST_ID = 'add-members-success'

type AddMembersDialogProps = Readonly<{
  open: boolean
  orgId: string
  canonicalOrgId?: string
  onClose: () => void
  onExitComplete: () => void
}>

type InlineError = {
  title: string
  description: string
}

function toInlineError(error: unknown, t: TFunction): InlineError {
  if (error instanceof ApiError && error.status === 400) {
    const message: unknown = error.body?.message
    return {
      title: String(t(`${KEY_PREFIX}.errors.badRequest.title`)),
      description:
        typeof message === 'string' && message !== ''
          ? message
          : String(t(`${KEY_PREFIX}.errors.badRequest.description`)),
    }
  }
  if (error instanceof ApiError && error.status === 404) {
    return {
      title: String(t(`${KEY_PREFIX}.errors.notFound.title`)),
      description: String(t(`${KEY_PREFIX}.errors.notFound.description`)),
    }
  }
  return {
    title: String(t(`${KEY_PREFIX}.errors.server.title`)),
    description: String(t(`${KEY_PREFIX}.errors.server.description`)),
  }
}

export function AddMembersDialog({
  open,
  orgId,
  canonicalOrgId,
  onClose,
  onExitComplete,
}: AddMembersDialogProps) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()

  const memberOrgIds = useMemo(
    () => (canonicalOrgId ? [orgId, canonicalOrgId] : [orgId]),
    [orgId, canonicalOrgId],
  )
  const { rows, entryError, addEmail, removeRow, retryLookup, updateRow } =
    useMemberRows(memberOrgIds)

  const [view, setView] = useState<'entry' | 'results'>('entry')
  const [outcomes, setOutcomes] = useState<MemberOutcome[]>([])
  const [inlineError, setInlineError] = useState<InlineError | undefined>()
  const submittedRows = useRef<MemberRow[]>([])

  const addMembers = useAddOrganizationMembers({
    onSuccess: (data) => {
      const paired = pairResultsWithRows(submittedRows.current, data.results)
      setOutcomes(paired)
      setView('results')
      if (isEveryMemberAdded(paired)) {
        showOrUpdateToast(SUCCESS_TOAST_ID, {
          ...SUCCESS_TOAST_OPTION,
          title: String(t(`${KEY_PREFIX}.toast.success`)),
        })
      }
      if (shouldRefreshMembers(paired)) {
        void invalidateOrgUsers(queryClient, orgId)
      }
    },
    onError: (error) => {
      setInlineError(toInlineError(error, t))
    },
  })

  const isPending = addMembers.isPending

  function handleSubmit() {
    setInlineError(undefined)
    submittedRows.current = rows
    addMembers.mutate({
      pathParams: { orgId },
      body: buildAddMembersRequest(rows),
    })
  }

  function handleClose() {
    if (isPending) {
      return
    }
    onClose()
  }

  return (
    <Dialog.Root
      open={open}
      lazyMount
      unmountOnExit
      onExitComplete={onExitComplete}
      size="lg"
      closeOnEscape={!isPending}
      closeOnInteractOutside={!isPending}
      onOpenChange={(details) => {
        if (!details.open) {
          handleClose()
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header pb={3}>
              <Dialog.Title>{t(`${KEY_PREFIX}.dialog.title`)}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton disabled={isPending} />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            {view === 'results' ? (
              <>
                <Dialog.Body>
                  <AddMembersResults outcomes={outcomes} />
                </Dialog.Body>
                <Dialog.Footer>
                  <Button colorPalette="brand" onClick={onClose}>
                    {t(`${KEY_PREFIX}.dialog.close`)}
                  </Button>
                </Dialog.Footer>
              </>
            ) : (
              <>
                <Dialog.Body>
                  <Stack gap={4}>
                    <Text fontSize="sm">
                      {t(`${KEY_PREFIX}.dialog.description`)}
                    </Text>
                    {inlineError && (
                      <Alert.Root status="error">
                        <Alert.Indicator />
                        <Alert.Content>
                          <Alert.Title>{inlineError.title}</Alert.Title>
                          <Alert.Description>
                            {inlineError.description}
                          </Alert.Description>
                        </Alert.Content>
                      </Alert.Root>
                    )}
                    <MembersEditor
                      rows={rows}
                      entryError={entryError}
                      disabled={isPending}
                      onCommit={addEmail}
                      onChange={updateRow}
                      onRemove={removeRow}
                      onRetry={retryLookup}
                    />
                  </Stack>
                </Dialog.Body>
                <Dialog.Footer>
                  <HStack gap={2}>
                    <Button
                      variant="outline"
                      disabled={isPending}
                      onClick={handleClose}
                    >
                      {t(`${KEY_PREFIX}.dialog.cancel`)}
                    </Button>
                    <Button
                      colorPalette="brand"
                      loading={isPending}
                      disabled={!canSubmitRows(rows)}
                      onClick={handleSubmit}
                    >
                      {t(`${KEY_PREFIX}.dialog.submit`)}
                    </Button>
                  </HStack>
                </Dialog.Footer>
              </>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
